<script setup>
import DOMPurify from 'dompurify';

import { capitalize, cloneDeep, isEqual, replace, sortBy } from 'lodash-es';
import HawkTable from '~/common/components/organisms/hawk-table/hawk-table.vue';
import { convertTableUnitsData } from '~/common/utils/common.utils.js';
import { useDashboardStore } from '~/dashboard/store/dashboard.store.js';
import { useDashboardScheduleStore } from '~/dashboard/store/dashboard-schedule.store.js';

const props = defineProps({
  data: {
    type: Object,
  },
  id: {
    type: String,
  },
  // eslint-disable-next-line vue/prop-name-casing
  content_height: {
    type: Number,
  },
});

const $services = inject('$services');

const dashboard_schedule_store = useDashboardScheduleStore();
const dashboard_store = useDashboardStore();

const force_update = ref(0);
const activities = ref([]);
const columns = ref([]);
const loading = ref(false);
const no_data = ref(false);
const payload = ref(null);
const prevent_watcher = ref(false);
const accessorKey = ref(null);
const nested_headers = ref([]);

const height = computed(() => {
  return ((props.data.h || 22) * 20) - 44;
});

const widget_data = computed(() => props.data?.data || {});

const colHeaders = function (index) {
  return columns.value[index].text;
};
async function getReports() {
  loading.value = true;
  activities.value = Object.assign([]);
  columns.value = Object.assign([]);
  nested_headers.value = Object.assign([]);

  const widget_data_copy = cloneDeep(props.data.data);
  payload.value = dashboard_schedule_store.parse_schedule_form_to_server_format(widget_data_copy);
  accessorKey.value = payload.value?.data?.interval?.name;

  try {
    const { data } = await $services.project_management.get_graph({ body: payload.value });
    if (data.data && Object.keys(data.data).length) {
      generateActivities(data.data);
      no_data.value = false;
    }
    else {
      no_data.value = true;
      columns.value = [];
      activities.value = [];
    }
    loading.value = false;
  }
  catch {
    loading.value = false;
  }
}

function hotSettings() {
  return {
    rowHeaders: true,
    rowHeights: 26,
    viewPortRowRenderingOffset: 100,
    viewportColumnRenderingOffset: 40,
    manualRowMove: false,
    contextMenu: false,
    readOnly: true,
  };
}

function set_color(value) {
  const numericValue = Number.parseFloat(String(value).replace(/[$,%]/g, ''));
  if (!Number.isNaN(numericValue)) {
    if (props.data.data.planned_vs_actual_table_tracking === 'progress') {
      if (numericValue > 0) {
        return DOMPurify.sanitize(`<span class="text-green-500">${value}</span>`);
      }
      else if (numericValue < 0) {
        return DOMPurify.sanitize(`<span class="text-red-500">${value}</span>`);
      }
    }

    else if (props.data.data.planned_vs_actual_table_tracking === 'cost') {
      if (numericValue < 0) {
        return DOMPurify.sanitize(`<span class="text-green-500">${value}</span>`);
      }
      else if (numericValue > 0) {
        return DOMPurify.sanitize(`<span class="text-red-500">${value}</span>`);
      }
    }
  }
  return value;
}

function cells(row, col, _x) {
  const cellProperties = {};
  const _value = this.instance.getDataAtCell(row, col);

  cellProperties.renderer = 'html';
  return cellProperties;
}

function generateActivities(data) {
  columns.value = [
    {
      accessorKey: 'name',
      header: 'Activity',
      id: 'name',
      size: '200',
      data: 'name',
      text: 'Activity',
    },
  ];

  const itemProperties = new Set();
  data[0]?.items?.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'label' && key !== 'date') {
        itemProperties.add(key);
      }
    });
  });

  const first_level = [{ label: 'Activity', colspan: 1 }];
  const second_level = [{ label: '', colspan: 1 }];

  data[0]?.items?.forEach((item) => {
    first_level.push({
      label: item.label,
      colspan: itemProperties.size,
    });

    itemProperties.forEach((prop) => {
      const columnKey = `${item.label.toLowerCase().replace(/\s+/g, '_')}_${prop}`;
      columns.value.push({
        accessorKey: columnKey,
        header: capitalize(prop.split('_').join(' ')),
        id: columnKey,
        size: '120',
        text: capitalize(prop.split('_').join(' ')),
        data: columnKey,
      });

      second_level.push({
        label: capitalize(prop.split('_').join(' ')),
        colspan: 1,
      });
    });
  });

  activities.value = [];

  data.forEach((project) => {
    const activity_object = {
      name: project.name,
    };

    project?.items?.forEach((item) => {
      itemProperties.forEach((prop) => {
        const columnKey = `${item.label.toLowerCase().replace(/\s+/g, '_')}_${prop}`;
        if (prop === 'difference') {
          activity_object[columnKey] = set_color(item[prop]) || '-';
        }
        else {
          activity_object[columnKey] = item[prop] || '-';
        }
      });
    });

    activities.value.push(activity_object);
  });

  if (payload.value?.properties?.activity_table_activities?.length) {
    const selectedActivities = payload.value.properties.activity_table_activities;
    activities.value = sortBy(activities.value, (activity) => {
      return selectedActivities.findIndex(selected => selected.name === activity.name);
    });
  }

  nested_headers.value = [first_level, second_level];
}

function columnResized(_resized_column, columns_widths) {
  prevent_watcher.value = true;
  dashboard_store.set_table_column_widths(
    props?.id,
    columns_widths,
  );
}
function updatePrintMap() {
  dashboard_store.update_print_map(props.id, {
    type: widget_data.value.type,
    renderAt: `chart-container-${props?.id}`,
    renderType: 'table',
    width: '100%',
    height: '100%',
    dataFormat: 'json',
    chart_name: widget_data.value.name,
    dimensions: {
      x: props.data.x,
      y: props.data.y,
    },
    dataSource: {
      dashboard_index: props.data.i,
      dataset: activities.value,
      columns: columns.value,
      is_transpose: false,
    },
  });
}

watch(() => props.data.data, async (new_val, old_val) => {
  if (new_val && !prevent_watcher.value) {
    if (old_val && isEqual(new_val, old_val)) {
      return;
    }

    prevent_watcher.value = true;
    await getReports();
    if (props?.id !== 'preview')
      updatePrintMap();
    prevent_watcher.value = false;
  }
}, { immediate: true, deep: true });

onMounted(() => {
  if (props.data.data && !prevent_watcher.value) {
    getReports();
  }
});
</script>

<template>
  <div>
    <div class="w-full h-full">
      <div v-if="$slots['header-title'] || $slots['header-actions']" class="widget-header group">
        <slot name="header-title" />
        <slot name="header-actions" />
      </div>

      <hawk-loader v-if="loading" />
      <div v-else-if="no_data || !activities?.length" class="text-sm font-semiBold w-full" :class="dashboard_store.is_mobile_view ? 'h-[240px] grid place-items-center' : 'mt-8 flex justify-center'">
        {{ $t('No data present') }}
      </div>
      <a v-else-if="activities && activities?.length && columns?.length">

        <div class="w-full scrollbar" :style="{ height: `${content_height || height}px` }">
          <HawkHandsontable
            :key="force_update"
            :apply-read-only-class="true"
            :data="activities"
            :columns="columns"
            :hot-settings="hotSettings()"
            :hot-table-id="id"
            :col-headers="colHeaders"
            :nested-headers="payload?.properties?.activity_table_activities?.length ? nested_headers : []"
            :height="`${content_height || height}px`"
            :cells="cells"
            @after-columns-resized="columnResized"
          />
        </div>
      </a>
    </div>
  </div>
</template>
